/**
 * Visão Tabela do setor: o PainelTabela das telas legadas de planilha (o
 * mesmo filtro por coluna estilo Excel, ordenação, redimensionar e
 * exportar), com a escolha de colunas guardada nas preferências.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiColumns } from "react-icons/fi";
import styled from "styled-components";

import PainelTabela from "../../../components/PainelTabela";
import { mq } from "../../../styles/canonical/breakpoints";
import {
  COLUNAS_TABELA,
  colunasVisiveis,
  definicaoDasColunas,
  labelDaContagem,
  montarLinhas,
  tituloDaTabela,
} from "./colunasTabela";
import { BotaoBarra } from "./styles";

const Envelope = styled.div`
  min-width: 0;
  /* Rede de segurança do "zero scroll lateral": clip corta o que passar da
     largura sem criar caixa de rolagem — o cabeçalho sticky da tabela
     continua funcionando (o que overflow hidden quebraria). */
  overflow-x: clip;

  /* Abaixo de 1200 px as colunas não cabem sem virar tiras de 4 px: a
     tabela mantém a largura de leitura e rola de lado DENTRO do painel,
     como uma planilha — a página continua sem scroll lateral. */
  @media (max-width: 1200px) {
    overflow-x: auto;

    table {
      min-width: 1400px;
    }
  }

  /* ≤1024 px o PainelTabela já vira cards; se alguma tabela sobrar (ex.:
     modo export), ela encolhe na largura em vez de rolar de lado. */
  @media ${mq.tablet} {
    overflow-x: clip;

    table {
      min-width: 0;
    }
  }

  /* A página já dá o respiro e a largura total: a tabela ocupa tudo. */
  > [data-page-container] {
    max-width: none;
    margin: 0;
    padding: 0;
  }
  > [data-page-container] h1 {
    font-size: 16px;
    font-weight: 700;
  }
`;

const Seletor = styled.div`
  position: relative;

  > [role="dialog"] {
    position: absolute;
    right: 0;
    top: calc(100% + 6px);
    z-index: 40;
    width: 280px;
    max-height: 380px;
    overflow-y: auto;
    padding: 10px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--surface);
    box-shadow: var(--shadow-3);
  }
  .cabeca {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
    font-size: 12.5px;
    font-weight: 700;
  }
  label {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    padding: 0 4px;
    font-size: 12.5px;
    color: var(--ink);
    cursor: pointer;
  }
  label[data-fixa="sim"] {
    cursor: default;
    color: var(--ink-3);
  }
  input[type="checkbox"] {
    accent-color: var(--accent);
    width: 16px;
    height: 16px;
  }
`;

function SeletorColunas({ entidade, visiveis, onChange }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e) => {
      if (!ref.current?.contains(e.target)) setAberto(false);
    };
    const esc = (e) => e.key === "Escape" && setAberto(false);
    document.addEventListener("mousedown", fora);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", fora);
      document.removeEventListener("keydown", esc);
    };
  }, [aberto]);

  const definicao = COLUNAS_TABELA[entidade] || [];
  const marcadas = new Set(visiveis);
  const alternar = (key) => {
    const proximas = new Set(marcadas);
    if (proximas.has(key)) proximas.delete(key);
    else proximas.add(key);
    onChange(definicao.filter((c) => proximas.has(c.key)).map((c) => c.key));
  };

  return (
    <Seletor ref={ref}>
      <BotaoBarra type="button" aria-expanded={aberto} onClick={() => setAberto((v) => !v)}>
        <FiColumns size={14} aria-hidden="true" /> Colunas
        <span className="n" style={{ fontFamily: "var(--font-mono)", fontWeight: 500, color: "var(--ink-3)" }}>
          {visiveis.length}/{definicao.length}
        </span>
      </BotaoBarra>
      {aberto && (
        <div role="dialog" aria-label="Colunas visíveis">
          <div className="cabeca">
            <span>Colunas visíveis</span>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => onChange([])}>
              Padrão
            </button>
          </div>
          {definicao.map((c) => (
            <label key={c.key} data-fixa={c.fixa ? "sim" : undefined}>
              <input type="checkbox" checked={marcadas.has(c.key)} disabled={c.fixa} onChange={() => alternar(c.key)} />
              {c.label}
            </label>
          ))}
        </div>
      )}
    </Seletor>
  );
}

/**
 * Props: entidade, setorId, setorNome, demandas, tarefas, pecas, indice,
 * hoje, colunasSalvas (preferência), onMudarColunas(keys),
 * onAbrir({tipo, item}).
 */
export default function Tabela({
  entidade,
  setorId,
  setorNome,
  demandas,
  tarefas,
  pecas,
  indice,
  hoje,
  colunasSalvas,
  onMudarColunas,
  onAbrir,
}) {
  const keys = useMemo(() => colunasVisiveis(entidade, colunasSalvas), [entidade, colunasSalvas]);
  const colunas = useMemo(() => definicaoDasColunas(entidade, keys), [entidade, keys]);
  const { linhas, itemPorId } = useMemo(
    () => montarLinhas(entidade, { demandas, tarefas, pecas, indice, hoje }),
    [entidade, demandas, tarefas, pecas, indice, hoje]
  );

  return (
    <Envelope>
      <PainelTabela
        key={entidade}
        titulo={tituloDaTabela(entidade, setorNome)}
        exportPainelId={`mapa-producao-${setorId}-${entidade}`}
        colunas={colunas}
        linhas={linhas}
        labelContagem={labelDaContagem(entidade)}
        mostrarPaginacao={false}
        mostrarTotalizadores={false}
        // A busca da página já filtra tudo (inclusive esta tabela).
        mostrarBusca={false}
        visualPlanilha
        persistirNoBackend
        filtrosExtras={
          <SeletorColunas entidade={entidade} visiveis={keys} onChange={onMudarColunas} />
        }
        onRowClick={(linha) => {
          const alvo = itemPorId.get(linha.id);
          if (alvo) onAbrir(alvo);
        }}
      />
    </Envelope>
  );
}
